// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import { useMemo } from 'react'
import { useQuery } from './useQuery'
import { useMutation } from './useMutation'
import type { QueryOperator } from '../../../core/operators/QueryOperator'
import type { MutationOperator } from '../../../core/operators/MutationOperator'
import type { UseMutationResult } from './useMutation'
import type { QueryResult } from '../../../core/operators/types'

/**
 * Query options accepted by Operator.query() methods.
 */
export interface CollectionQueryOptions {
  sort?: string
  limit?: number
  skip?: number
}

/**
 * Minimum interface any Operator class passed to createCollectionHooks must satisfy.
 */
export interface CollectionOperatorInterface<TModel, TFilters, TCreate, TUpdate> {
  query(filters: TFilters, options?: CollectionQueryOptions): QueryOperator<TModel>
  createMutation(): MutationOperator<TModel, TCreate>
  updateMutation(): MutationOperator<TModel, TUpdate>
  deleteMutation(): MutationOperator<void, string>
}

/**
 * Constructor type for classes implementing CollectionOperatorInterface.
 */
export type CollectionOperatorClass<TModel, TFilters, TCreate, TUpdate> = new (
  db: any
) => CollectionOperatorInterface<TModel, TFilters, TCreate, TUpdate>

/**
 * The four hooks returned by createCollectionHooks.
 */
export interface CollectionHooks<TModel, TFilters, TCreate, TUpdate> {
  /**
   * Query hook. Returns { data, isLoading, error }.
   * Returns empty/not-loading when db or filters is null/undefined.
   */
  useItems: (db: any, filters: TFilters, options?: CollectionQueryOptions) => QueryResult<TModel>
  /** Create mutation hook. Returns UseMutationResult. */
  useCreate: (db: any) => UseMutationResult<TModel, TCreate>
  /** Update mutation hook. Returns UseMutationResult. */
  useUpdate: (db: any) => UseMutationResult<TModel, TUpdate>
  /** Soft-delete mutation hook. Returns UseMutationResult<void, string>. */
  useDelete: (db: any) => UseMutationResult<void, string>
}

/**
 * Factory that creates React hooks for a collection operator class.
 *
 * Replaces per-entity boilerplate hook files (useProjects.ts, useMilestones.ts,
 * etc.) by generating a typed hook set from any operator class that satisfies
 * CollectionOperatorInterface.
 *
 * The returned hooks accept `db` as their first argument (Layer 1 pattern).
 * App-level code wraps them calling useDatabase() internally (Layer 2 pattern).
 *
 * @param OperatorClass - Any operator class constructor (e.g., ProjectOperator)
 * @returns { useItems, useCreate, useUpdate, useDelete }
 *
 * @example
 * ```ts
 * import { createCollectionHooks, MilestoneOperator } from '@epicdm/flowstate-collections/react'
 * import { useDatabase, useOrgWorkspace } from '@epicdm/flowstate-app-framework'
 *
 * const { useItems, useCreate, useUpdate, useDelete } = createCollectionHooks(MilestoneOperator)
 *
 * export function useProjectMilestones(projectId: string) {
 *   const { db } = useDatabase()
 *   const { currentOrgId } = useOrgWorkspace()
 *   return useItems(db, { orgId: currentOrgId, projectId, archived: false })
 * }
 *
 * export function useCreateMilestone() {
 *   const { db } = useDatabase()
 *   return useCreate(db)
 * }
 * ```
 */
export function createCollectionHooks<TModel, TFilters, TCreate, TUpdate>(
  OperatorClass: CollectionOperatorClass<TModel, TFilters, TCreate, TUpdate>
): CollectionHooks<TModel, TFilters, TCreate, TUpdate> {
  function useItems(
    db: any,
    filters: TFilters,
    options?: CollectionQueryOptions
  ): QueryResult<TModel> {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const queryOperator = useMemo(() => {
      if (!db || !filters) return null
      try {
        return new OperatorClass(db).query(filters, options)
      } catch {
        return null
      }
      // JSON.stringify is intentional — filters and options are plain objects
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [db, JSON.stringify(filters), JSON.stringify(options)])

    return useQuery<TModel>(queryOperator)
  }

  function useCreate(db: any): UseMutationResult<TModel, TCreate> {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const mutationOperator = useMemo(
      () => (db ? new OperatorClass(db).createMutation() : null),
      [db]
    )
    return useMutation<TModel, TCreate>(mutationOperator)
  }

  function useUpdate(db: any): UseMutationResult<TModel, TUpdate> {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const mutationOperator = useMemo(
      () => (db ? new OperatorClass(db).updateMutation() : null),
      [db]
    )
    return useMutation<TModel, TUpdate>(mutationOperator)
  }

  function useDelete(db: any): UseMutationResult<void, string> {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const mutationOperator = useMemo(
      () => (db ? new OperatorClass(db).deleteMutation() : null),
      [db]
    )
    return useMutation<void, string>(mutationOperator)
  }

  return { useItems, useCreate, useUpdate, useDelete }
}
